import { create } from "zustand";
import type { GuideQuoteLocator, GuideQuotedReference } from "../../../constants/GlobalTypesAndEnumsAndConstants";

/**
 * Passages or visuals the learner chose to ask about (study guide "ask a
 * question"). The composer shows them as quote chips; the next typed message
 * is sent with each one quoted above it, then they clear. They live only in
 * memory for the current reader.
 */
export type ChatQuotedReference = {
  id: string;
  /** What the tutor reads: the passage, or a text rendering of a visual. */
  quote: string;
  deckId: string;
  /** A passage of text, or a figure/table/process the reader pointed at. */
  kind: "text" | "visual";
  /** Short visible name for a visual ("table: The two sides of the heart"). */
  label?: string;
  /** Thumbnail for the quote chip; the selected pixels travel as a separate attachment. */
  imageUrl?: string;
  /** Where it is in the guide, so the sent message can bring the reader back to it. */
  locator?: GuideQuoteLocator;
};

export const MAX_QUOTED_REFERENCES = 4;

type ChatQuotedReferenceState = {
  references: ChatQuotedReference[];
  /** Bumped on each new reference so the composer refocuses even for the same words. */
  revision: number;
  /** Adds a reference (the same words once); past the limit the oldest is dropped. */
  addReference: (reference: Omit<ChatQuotedReference, "id">) => void;
  removeReference: (id: string) => void;
  clearReferences: () => void;
};

let nextReferenceId = 0;

export const useChatQuotedReferenceStore = create<ChatQuotedReferenceState>((set) => ({
  references: [],
  revision: 0,
  addReference: (reference) => set((state) => {
    // Asking about a passage in another guide starts a fresh set.
    const sameDeck = state.references.filter((item) => item.deckId === reference.deckId && item.quote !== reference.quote);
    const references = [...sameDeck, { ...reference, id: `ref-${++nextReferenceId}` }].slice(-MAX_QUOTED_REFERENCES);
    return { references, revision: state.revision + 1 };
  }),
  removeReference: (id) => set((state) => ({ references: state.references.filter((item) => item.id !== id) })),
  clearReferences: () => set({ references: [] }),
}));

const QUOTE_LIMIT = 700;
const VISUAL_LIMIT = 1600;

function quoteBlock(quote: string, limit: number) {
  // Visuals keep their line structure (a table's rows); passages are one flowing line.
  const lines = limit === VISUAL_LIMIT ? quote.trim().split("\n").map((line) => line.trimEnd()) : [quote.replace(/\s+/gu, " ").trim()];
  let text = lines.join("\n");
  if (text.length > limit) text = `${text.slice(0, limit - 1).trimEnd()}…`;
  return text.split("\n").map((line) => `> ${line}`).join("\n");
}

/**
 * The question with each referenced passage quoted above it (markdown
 * blockquotes the chat renders). Accepts one passage or several references.
 */
export function composeQuotedQuestion(quotes: string | Pick<ChatQuotedReference, "quote" | "kind">[], question: string) {
  const items = typeof quotes === "string" ? [{ quote: quotes, kind: "text" as const }] : quotes;
  const blocks = items.map((item) => quoteBlock(item.quote, item.kind === "visual" ? VISUAL_LIMIT : QUOTE_LIMIT));
  return `${blocks.join("\n\n")}\n\n${question.trim()}`;
}

/**
 * Attaches the pending references to a typed message for this deck and clears
 * them. Messages for another deck, or with no reference, pass through unchanged.
 */
export function takeQuotedReferences(message: string, deckId: string | undefined): { message: string; references: ChatQuotedReference[] } {
  const { references, clearReferences } = useChatQuotedReferenceStore.getState();
  const mine = deckId ? references.filter((item) => item.deckId === deckId) : [];
  if (!mine.length) return { message, references: [] };
  clearReferences();
  return { message: composeQuotedQuestion(mine, message), references: mine };
}

/** The references as a sent message keeps them: what was quoted and where it is in the guide. */
export function storedQuotedReferences(references: Pick<ChatQuotedReference, "quote" | "kind" | "locator">[]): GuideQuotedReference[] {
  return references.map((reference) => ({
    kind: reference.kind,
    // A passage keeps its words (they re-find it if the guide changed); a visual is found by its id.
    quote: reference.quote.replace(/\s+/gu, " ").trim().slice(0, reference.kind === "visual" ? 200 : QUOTE_LIMIT),
    ...(reference.locator ? { locator: reference.locator } : {}),
  }));
}

// A question sent straight from the study guide (explain it to me, an analogy, a mnemonic) arrives at
// the chat as text with its quote already above it: its references wait here, keyed by that text.
const referencesBySentMessage = new Map<string, GuideQuotedReference[]>();

export function rememberQuotedReferences(deckId: string, message: string, references: GuideQuotedReference[]) {
  if (!references.length) return;
  referencesBySentMessage.set(`${deckId}\n${message.trim()}`, references);
  while (referencesBySentMessage.size > 20) referencesBySentMessage.delete(referencesBySentMessage.keys().next().value as string);
}

export function takeRememberedQuotedReferences(deckId: string | undefined, message: string): GuideQuotedReference[] {
  if (!deckId) return [];
  const key = `${deckId}\n${message.trim()}`;
  const references = referencesBySentMessage.get(key) ?? [];
  referencesBySentMessage.delete(key);
  return references;
}

// Compared without markdown (quote marks, table pipes and rules, emphasis) and spacing.
const comparable = (text: string) => text.replace(/^>\s?/gmu, "").replace(/\|\s*-{3,}\s*/gu, " ").replace(/[|“”"‘’'*_`]/gu, " ")
  .replace(/\s+/gu, " ").trim().toLowerCase();

/** The reference a quote block in a sent message stands for (by its words), if the message kept one. */
export function matchQuotedReference(blockText: string, references: GuideQuotedReference[] | undefined): GuideQuotedReference | null {
  if (!references?.length) return null;
  const block = comparable(blockText).replace(/…$/u, "");
  if (!block) return null;
  for (const reference of references) {
    const quote = comparable(reference.quote).replace(/…$/u, "");
    const head = Math.min(48, quote.length, block.length);
    if (head && (block.slice(0, head) === quote.slice(0, head))) return reference;
  }
  return null;
}
